/**
 * CR-7714 FE-9: the rows of the dashboard CSV export.
 *
 * Extracted from useDashboardData so the export rule is pure and tested. The
 * rule: a figure the dashboard does not have is an EMPTY cell. The old export
 * wrote `otif || 0` in the trend series and `${ppm}` in the headline, so a
 * month with no deliveries told management "0", and a missing PPM printed the
 * literal text "null". Percentages keep the KPI decimal setting (CR-6155).
 */
import type { DashboardData } from '../components/dashboard/types';
import { formatKpiPercent, roundKpiPercentColumns } from '../lib/kpiPercent';

/** A value as a CSV cell: '' when absent (null / undefined / NaN), else the value. */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return String(value);
}

type Row = Record<string, unknown>;

/** Append a titled chart series, one CSV line per point. Nothing when the series is empty. */
function series(rows: string[], title: string, header: string, items: Row[] | undefined, cols: string[]): void {
  if (!items || items.length === 0) return;
  rows.push(title, header);
  items.forEach((item) => {
    rows.push(cols.map((c) => csvCell(item[c])).join(','));
  });
  rows.push('');
}

/**
 * The CSV lines for the main (non-compliance) dashboard export.
 *
 * @param data - The dashboard payload
 * @param kpiDecimals - The company's KPI percent decimal setting
 * @returns CSV lines, blank cells where the dashboard has no figure
 */
export function buildDashboardCsvRows(data: DashboardData, kpiDecimals: number): string[] {
  // The headline OTIF/OTD keep "N/A" for a missing figure, as before (CR-6155).
  const pct = (value: number | null | undefined) => formatKpiPercent(value, kpiDecimals);
  const rows: string[] = [];
  rows.push('Quality Metrics');
  rows.push(
    `PPM,${csvCell(data.quality.ppm)}`, `PPM Target,${csvCell(data.quality.ppmTarget)}`, `NCRs,${csvCell(data.quality.ncrs)}`,
    `OTIF,${pct(data.quality.otif)}`, `OTIF Target,${pct(data.quality.otifTarget)}`, '',
  );

  rows.push('Delivery Metrics');
  rows.push(
    `OTD,${pct(data.delivery.otd)}`, `OTD Target,${pct(data.delivery.otdTarget)}`, `Appeals,${csvCell(data.delivery.appeals)}`,
    `Open Order Value,${csvCell(data.delivery.openOrderValue)}`, '',
  );

  rows.push('Cost Metrics');
  rows.push(`Total Spend,${csvCell(data.cost.totalSpend)}`, `Historical Arrears,${csvCell(data.cost.historicalArrears)}`, '');

  rows.push('Purchasing');
  rows.push(
    `Total POs,${csvCell(data.purchasing.totalPOs)}`, `Total Lines,${csvCell(data.purchasing.totalLines)}`,
    `Total Value,${csvCell(data.purchasing.totalValue)}`, '',
  );

  rows.push('Suppliers');
  rows.push(`Total,${csvCell(data.suppliers.total)}`, `Active,${csvCell(data.suppliers.active)}`, `Strategic,${csvCell(data.suppliers.preferred)}`, '');

  const charts = data.charts as unknown as Record<string, Row[] | undefined>;
  const otifData = charts['otifData'] ? roundKpiPercentColumns(charts['otifData'], kpiDecimals) : undefined;
  const otdData = charts['otdData'] ? roundKpiPercentColumns(charts['otdData'], kpiDecimals) : undefined;
  series(rows, 'PPM Trend', 'Month,PPM,Target', charts['ppmTrend'], ['month', 'ppm', 'target']);
  series(rows, 'OTIF Trend', 'Month,OTIF %,Target,OTIF Lines,Arrears Lines,Failed Lines', otifData,
    ['month', 'otif', 'target', 'otifCount', 'arrears', 'failed']);
  series(rows, 'OTD Trend', 'Month,OTD %,Target,On Time,Early,Late', otdData,
    ['month', 'otd', 'target', 'onTime', 'early', 'late']);
  series(rows, 'Spend by Month', 'Month,Spend', charts['spendByMonth'], ['month', 'spend']);
  return rows;
}
